if (!customElements.get('terms-checkbox')) {
  class TermsAndConditionsToggle extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.container = this.closest('.cart__details');

      if (!this.checkbox || !this.container) return;

      this.controller?.abort();
      this.controller = new AbortController();

      const options = {
        signal: this.controller.signal
      };

      const messages = {
        en: 'You must agree to the terms and conditions of sale to proceed to checkout.',
        bg: 'За да продължите към плащане, трябва да приемете общите условия за продажба.',
        de: 'Sie müssen den Allgemeinen Geschäftsbedingungen zustimmen, um zur Kasse zu gehen.',
        fr: 'Vous devez accepter les conditions générales de vente pour procéder au paiement.',
        it: 'Devi accettare le condizioni generali di vendita per procedere al pagamento.',
        es: 'Debes aceptar las condiciones generales de venta para proceder al pago.',
        cs: 'Chcete-li pokračovat k platbě, musíte souhlasit s obchodními podmínkami.',
        da: 'Du skal acceptere handelsbetingelserne for at fortsætte til betaling.',
        el: 'Πρέπει να αποδεχτείτε τους όρους και τις προϋποθέσεις πώλησης για να προχωρήσετε στην πληρωμή.',
        fi: 'Sinun on hyväksyttävä myyntiehdot, jotta voit jatkaa maksamiseen.',
        hu: 'A fizetéshez el kell fogadnia az általános szerződési feltételeket.',
        nl: 'Je moet akkoord gaan met de algemene verkoopvoorwaarden om door te gaan naar het afrekenen.',
        no: 'Du må godta salgsbetingelsene for å fortsette til betaling.',
        pl: 'Aby przejść do płatności, musisz zaakceptować ogólne warunki sprzedaży.',
        pt: 'Tem de aceitar os termos e condições de venda para prosseguir para o pagamento.',
        ro: 'Trebuie să acceptați termenii și condițiile de vânzare pentru a continua la plată.',
        sv: 'Du måste godkänna försäljningsvillkoren för att gå vidare till betalning.',
        ja: '購入手続きに進むには、販売条件に同意する必要があります。'
      };

      const language = (
        document.documentElement.lang || 'en'
      ).toLowerCase().split(/[-_]/)[0];

      const normalizedLanguage =
        language === 'nb' || language === 'nn' ? 'no' : language;

      this.message = messages[normalizedLanguage] || messages.en;

      // Replace browser validation with our own warning.
      this.checkbox.required = false;
      this.checkbox.setCustomValidity('');
      this.checkbox.setAttribute('aria-required', 'true');

      if (!document.getElementById('tr-terms-warning-style')) {
        const style = document.createElement('style');
        style.id = 'tr-terms-warning-style';

        style.textContent = `
          terms-checkbox {
            display: block;
          }

          .tr-terms-warning {
            position: relative;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            margin: 12px 0;
            padding: 12px 14px;
            color: #000;
            background: #fff;
            border: 1px solid #000;
            border-radius: 5px;
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
            font-size: 13px;
            line-height: 1.45;
            text-align: start;
          }

          .tr-terms-warning[hidden] {
            display: none !important;
          }

          .tr-terms-warning::before {
            content: "";
            position: absolute;
            top: -6px;
            left: 18px;
            width: 10px;
            height: 10px;
            background: #fff;
            border-top: 1px solid #000;
            border-left: 1px solid #000;
            transform: rotate(45deg);
          }

          .tr-terms-warning__icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 22px;
            width: 22px;
            height: 22px;
            color: #fff;
            background: #000;
            border-radius: 3px;
            font-size: 17px;
            font-weight: 700;
            line-height: 1;
          }

          .tr-terms-warning__text {
            min-width: 0;
            overflow-wrap: anywhere;
          }
        `;

        document.head.appendChild(style);
      }

      this.warning?.remove();

      this.warning = document.createElement('div');
      this.warning.className = 'tr-terms-warning';
      this.warning.id = `tr-terms-warning-${++TermsAndConditionsToggle.count}`;
      this.warning.setAttribute('role', 'alert');
      this.warning.hidden = true;

      const icon = document.createElement('span');
      icon.className = 'tr-terms-warning__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '!';

      const text = document.createElement('span');
      text.className = 'tr-terms-warning__text';
      text.textContent = this.message;

      this.warning.append(icon, text);
      this.appendChild(this.warning);

      this.originalDescription =
        this.checkbox.getAttribute('aria-describedby');

      this.hideWarning = () => {
        this.warning.hidden = true;
        this.checkbox.removeAttribute('aria-invalid');

        if (this.originalDescription) {
          this.checkbox.setAttribute(
            'aria-describedby',
            this.originalDescription
          );
        } else {
          this.checkbox.removeAttribute('aria-describedby');
        }
      };

      this.container
        .querySelectorAll('button[name="checkout"]')
        .forEach(button => {
          button.disabled = false;
        });

      this.checkbox.addEventListener(
        'change',
        () => {
          if (this.checkbox.checked) {
            this.hideWarning();
          }
        },
        options
      );

      this.blockCheckout = event => {
        if (this.checkbox.checked) {
          this.hideWarning();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        this.warning.hidden = false;
        this.checkbox.setAttribute('aria-invalid', 'true');
        this.checkbox.setAttribute(
          'aria-describedby',
          [this.originalDescription, this.warning.id]
            .filter(Boolean)
            .join(' ')
        );

        this.checkbox.focus({ preventScroll: true });
        this.warning.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      };

      document.addEventListener(
        'click',
        event => {
          const button = event.target.closest?.(
            'button[name="checkout"]'
          );

          if (button && this.container.contains(button)) {
            this.blockCheckout(event);
          }
        },
        { ...options, capture: true }
      );

      document.addEventListener(
        'submit',
        event => {
          const button = event.submitter;

          if (
            button?.matches('button[name="checkout"]') &&
            this.container.contains(button)
          ) {
            this.blockCheckout(event);
          }
        },
        { ...options, capture: true }
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.hideWarning?.();
      this.warning?.remove();
    }
  }

  TermsAndConditionsToggle.count = 0;

  customElements.define(
    'terms-checkbox',
    TermsAndConditionsToggle
  );
}